/**
 * Course-ban document schemas and Firebase paths.
 *
 * A `courseBan` doc, when present and active for a `(user, course)` pair,
 * blocks that user's access to the course. The webapp renders a no-access
 * page when the doc is read on app load, and `firestore.rules` denies
 * forum writes from the banned user as a defense-in-depth check for the
 * most-abusable participation surface.
 *
 * The doc may have an `expiresAt` timestamp; once `expiresAt` is in the
 * past the ban is considered inactive even though the doc still exists
 * (history is preserved until staff explicitly delete it).
 *
 * Only course admins can create, update, or delete this doc. The target
 * user can read their own doc so the no-access page can render an appeal
 * link to the course's staff-contact email.
 *
 * Tracking: https://github.com/CodeinPlace/cip/issues/2865
 */

import {z} from 'zod';

import type {CourseId} from '../../../types/branded';
import {FirestoreTimestampSchema} from '../../types';

// ============================================================================
// Top-level schema
// ============================================================================

/**
 * Per-`(user, course)` course-ban document.
 *
 * Stored at: `users/{userId}/courseBans/{courseId}`. The doc id is the
 * `CourseId`, so the gate on app load is a single read by known path.
 *
 * Active vs. expired is a derived property: a doc with `expiresAt === null`
 * is indefinite-and-active; a doc with `expiresAt` set is active up to and
 * including the exact `expiresAt` instant (i.e. the upper bound of the
 * active window is inclusive). Callers must compare server time, not
 * client wall clock.
 *
 * `.loose()` so adding new top-level fields later (e.g. `liftedAt`,
 * `publicReason`, per-feature scope) doesn't break readers persisted
 * before the field existed.
 */
export const CourseBanDocumentSchema = z
  .object({
    /**
     * The course the ban applies to. Redundant with the doc id (which is
     * also the `CourseId`), but stored as a field so `collectionGroup`
     * queries can filter by course without resorting to
     * `FieldPath.documentId()` path-prefix tricks.
     */
    courseId: z.string(),
    /**
     * Staff-internal note about why the ban was issued. Not shown to the
     * banned user; surfaced only on the admin UI for moderation review.
     * The user-facing no-access page uses a hardcoded generic message
     * and points to the per-course `contactEmail` from the course's
     * theme document (see `useCourseTheme()` in the webapp), so
     * Foothill-branded courses get their own contact address.
     */
    reason: z.string(),
    /**
     * When the ban auto-lifts. `null` for indefinite bans. The ban is
     * considered active up to and including this instant; once the server
     * clock passes `expiresAt`, the doc is treated as inactive but is
     * retained for history.
     */
    expiresAt: FirestoreTimestampSchema.nullable(),
    /** Uid of the admin who issued the ban. */
    enforcerUid: z.string(),
    /** Server timestamp at first write. */
    createdAt: FirestoreTimestampSchema,
    /** Server timestamp at most recent update. */
    updatedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Per-`(user, course)` course-ban document. */
export type CourseBanDocument = z.infer<typeof CourseBanDocumentSchema>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Constructs the path to a user's course-ban document for a specific course.
 *
 * Document path: `users/{userId}/courseBans/{courseId}`.
 *
 * @param userId - The user the ban applies to.
 * @param courseId - The course in which the ban applies.
 * @returns The fully-qualified Firestore document path.
 */
export function getCourseBanDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: CourseId;
}): string {
  return `users/${userId}/courseBans/${courseId}`;
}

/**
 * Constructs the path to the `courseBans` collection for a user (across all
 * courses). Useful for admin tooling that needs to list every ban a user
 * has been given.
 *
 * Collection path: `users/{userId}/courseBans`.
 *
 * @param userId - The user whose courseBans collection is addressed.
 * @returns The fully-qualified Firestore collection path.
 */
export function getCourseBansCollectionPath({
  userId,
}: {
  userId: string;
}): string {
  return `users/${userId}/courseBans`;
}

/**
 * Collection ID for `courseBans` subcollections, suitable for
 * `collectionGroup` queries when listing bans across users (e.g. the
 * admin dashboard for "everyone banned in this course"). Pair with a
 * `where('courseId', '==', courseId)` filter on the doc — see the
 * `setCourseBan` callable (PR 3) for the canonical writer that stamps
 * `courseId` as a field for this purpose.
 */
export const COURSE_BANS_COLLECTION_ID = 'courseBans';
